/**
 * TaxSetu AI - Refund & Cancellation Policy Page
 * ================================================
 * Required for Razorpay verification
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw } from 'lucide-react';

export default function RefundPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="bg-[#0f2744] border-b border-[#1e3a5f] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">
            Tax<span className="text-[#38bdf8]">Setu</span> AI
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <RefreshCcw className="w-8 h-8 text-[#38bdf8]" />
          <h1 className="text-3xl font-bold text-white">Refund & Cancellation Policy</h1>
        </div>
        
        <div className="bg-[#0f2744] rounded-xl p-8 border border-[#1e3a5f] prose prose-invert max-w-none">
          <p className="text-gray-400 text-sm mb-6">Last Updated: January 19, 2026</p>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Subscription Cancellation</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You may cancel your TaxSetu AI Pro subscription at any time through your account settings 
              or by contacting our support team.
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>You will continue to have access to Pro features until your subscription expires</li>
              <li>No partial refunds are provided for unused portions of a subscription period</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Refund Policy</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">2.1 Eligibility for Refund</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              We offer refunds under the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Within 7 days of purchase:</strong> Full refund if you are not satisfied with the service (first-time subscribers only)</li>
              <li><strong>Technical issues:</strong> If the service was unavailable for an extended period due to our fault</li>
              <li><strong>Duplicate payments:</strong> Full refund for any accidental duplicate charges</li>
              <li><strong>Unauthorized transactions:</strong> Full refund after verification</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-2">2.2 Non-Refundable Cases</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              Refunds will NOT be provided for:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Requests made after 7 days of purchase</li>
              <li>Users who have violated our Terms of Service</li>
              <li>Users who have already received a refund previously</li>
              <li>Promotional or discounted subscriptions (unless specified)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. How to Request a Refund</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To request a refund, please follow these steps:
            </p>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>Email us at <span className="text-[#38bdf8]">support@taxsetu.ai</span> with subject line "Refund Request"</li>
              <li>Include your registered email address and transaction ID</li>
              <li>Provide the reason for your refund request</li>
              <li>Our team will review and respond within 3-5 business days</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. Refund Processing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Once approved:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Refunds are processed within 5-7 business days</li>
              <li>Amount will be credited to the original payment method</li>
              <li>Bank processing may take an additional 5-10 business days</li>
              <li>You will receive email confirmation once the refund is initiated</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Pricing Changes</h2>
            <p className="text-gray-300 leading-relaxed">
              We reserve the right to modify subscription pricing. Existing subscribers will be notified 
              30 days in advance of any price changes. Price changes will apply to renewals, not the 
              current subscription period.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              For refund or cancellation queries, please contact:
            </p>
            <div className="mt-4 p-4 bg-[#1a3654] rounded-lg">
              <p className="text-white font-medium">Credence Tax Advisors (OPC) Pvt. Ltd.</p>
              <p className="text-gray-400">Email: support@taxsetu.ai</p>
              <p className="text-gray-400">Phone: +91 9711162342</p>
              <p className="text-gray-400">Address: A-50, Street-3, New Modern Shahdara, Delhi-110032</p>
              <p className="text-gray-400 mt-2">Response Time: Within 24-48 hours</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
